import { List } from 'immutable';
import isInteger from 'lodash/isInteger';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { call, put, select } from 'redux-saga/effects';

import * as PlaybackConstants from '../../constants';
import * as D from '../../deps';
import * as PlaybackUtils from '../../utils';

const log = D.Logger.child({ component: 'domain/Playback/workers/Source' });

export function* annotateIds(ids = []) {
  yield call(D.putAndWait, D.Actions.Annotations.annotateIds(ids));
}

function* getTracksForPlaylist(id, defaultOffset = null) {
  const tracks = yield select(D.getInDomain('catalog.utils.getLoadedTracksForPlaylist'), id);
  const numberOfTracks = tracks.size;

  const offset = isNil(defaultOffset) ? numberOfTracks : defaultOffset;
  let version = yield select(D.getInDomain('catalog.utils.getVersionForPlaylist'), id);

  // Need to check if client has playlist tracks in state because
  // if the version is provided from the client and they match on the server, the tracks are omitted by design to get a faster response.
  // It's assuming that the client already has cached the tracks for that version. If the version is omitted or varies from the
  // version provided then it will fetch the tracks
  // Note: If an offset is provided, the versions have to match, otherwise no tracks will be fetched
  if (!isNil(version) && numberOfTracks === 0) {
    version = null;
  }

  yield call(
    D.putAndWait,
    D.Actions.Playlist.getTracksForPlaylist({ pandoraId: id, offset, version }),
  );
}

export function* getTracksForSource({ id, type, sortOrder }) {
  let trackList;
  const selector = D.getInDomain('catalog.utils.getTrackGetterBySourceType')(type);

  switch (type) {
    case D.Constants.SourceTypes.SourceTypes.SONG:
      trackList = List([id]);
      break;
    case D.Constants.SourceTypes.SourceTypes.ALBUM:
      trackList = yield call(D.getOrFetch, {
        selector,
        selectorArgs: [id],
        fetcher: annotateIds.bind(null, [id]),
      });
      break;
    case D.Constants.SourceTypes.SourceTypes.PLAYLIST:
      trackList = yield call(D.getOrFetch, {
        selector,
        selectorArgs: [id],
        fetcher: getTracksForPlaylist.bind(null, id),
      });
      break;
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_TRACKS:
      trackList = yield select(selector, id, sortOrder);
      break;
    case D.Constants.SourceTypes.SourceTypes.ARTIST_ALL_SONGS:
    case D.Constants.SourceTypes.SourceTypes.ARTIST_TOP_SONGS:
    case D.Constants.SourceTypes.SourceTypes.COLLECTED_ALBUM_TRACKS:
    case D.Constants.SourceTypes.SourceTypes.STATION_THUMB_UP:
      // this is just used for getting tracks
      // we do logic elsewhere for fetching these tracks
      trackList = yield select(selector, id);
      break;
  }

  if (!List.isList(trackList)) {
    log.error(
      `getTracksForSource: return value must be a List. Value: ${trackList} id: ${id} type: ${type}`,
    );
    return List();
  }

  return trackList;
}

function* alertRadioOnlyAlbum(annotations) {
  const { dispatch } = D.getStore();
  const artistAnnotations = yield select(
    D.Utils.Entity.getAnnotationsById,
    annotations.get('artistId'),
  );

  if (D.Utils.Rights.hasNoRadio(artistAnnotations)) {
    yield put(
      D.Actions.Modal.openModalCoachmark(PlaybackConstants.PlaybackMessages.radioOnlyAlbum),
    );
  } else {
    yield put(
      D.Actions.Modal.openModalCoachmarkWithButton({
        bodyMessage: <FormattedMessage {...PlaybackConstants.PlaybackMessages.radioOnlyAlbum} />,
        buttonMessage: (
          <FormattedMessage {...PlaybackConstants.PlaybackMessages.startArtistStation} />
        ),
        styleNames: ['notPlayableSource'],
        buttonCallback: () => {
          dispatch(D.Actions.Modal.closeModal());
          dispatch(
            D.Actions.UseCases.startStation({
              pandoraId: annotations.get('artistId'),
            }),
          );
        },
      }),
    );
  }
}

function* alertRadioOnlySong(annotations) {
  const { dispatch } = D.getStore();
  if (D.Utils.Rights.hasNoRadio(annotations)) {
    yield put(
      D.Actions.Modal.openModalCoachmark(PlaybackConstants.PlaybackMessages.unavailableSong),
    );
  } else {
    yield put(
      D.Actions.Modal.openModalCoachmarkWithButton({
        bodyMessage: <FormattedMessage {...PlaybackConstants.PlaybackMessages.radioOnlySong} />,
        buttonMessage: <FormattedMessage {...PlaybackConstants.PlaybackMessages.startStation} />,
        styleNames: ['notPlayableSource'],
        buttonCallback: () => {
          dispatch(D.Actions.Modal.closeModal());
          dispatch(
            D.Actions.UseCases.startStation({
              pandoraId: annotations.get('pandoraId'),
            }),
          );
        },
      }),
    );
  }
}

function* openRadioOnlyCoachmark(annotations, type) {
  if (type === D.Constants.SourceTypes.SourceTypes.ALBUM) {
    yield call(alertRadioOnlyAlbum, annotations);
  } else {
    yield call(alertRadioOnlySong, annotations);
  }
}

function* checkIsPlayableSourceAndAlertUser({
  id,
  sourceId,
  index,
  sortOrder,
  alertUserIfUnavailable,
}) {
  let isPlayable = true;
  const type = D.Utils.SourceType.getSourceTypeFromPandoraId(id);
  const annotations = yield select(D.Utils.Entity.getAnnotationsById, id);
  if (
    [D.Constants.SourceTypes.SourceTypes.SONG, D.Constants.SourceTypes.SourceTypes.ALBUM].includes(
      type,
    ) &&
    annotations?.size > 0
  ) {
    const playbackMessages = PlaybackConstants.PlaybackMessages;
    if (D.Utils.Rights.isUnavailable(annotations)) {
      if (alertUserIfUnavailable) {
        const message =
          type === D.Constants.SourceTypes.SourceTypes.ALBUM
            ? playbackMessages.unavailableAlbum
            : playbackMessages.unavailableSong;
        yield put(D.Actions.Modal.openModalCoachmark(message));
      }
      isPlayable = false;
    } else if (D.Utils.Rights.isRadioOnly(annotations)) {
      if (alertUserIfUnavailable) {
        yield call(openRadioOnlyCoachmark, annotations, type);
      }
      isPlayable = false;
    } else if (D.getInDomain('catalog.utils.isCatalogVoiceTrack')(annotations)) {
      const canPlayVoiceTrack = yield select(
        D.getInDomain('playback.utils.canPlayVoiceTrack'),
        annotations,
        sourceId,
      );
      if (alertUserIfUnavailable && !canPlayVoiceTrack) {
        const { dispatch } = D.getStore();
        const vt = PlaybackConstants.voiceTrackMessages;
        const trackIndex = yield select(PlaybackUtils.getTrackIndexInOriginalSource, {
          trackId: id,
          sourceId,
        });
        const modalConfig = {
          bodyMessage: <FormattedMessage {...vt.artistAudioMessagesOff} />,
          buttonMessage: <FormattedMessage {...vt.artistAudioMessagesTurnOn} />,
          buttonCallback: () => {
            dispatch(D.Actions.Modal.closeModal());
            dispatch(
              D.Actions.Playlist.setAreVoiceTracksEnabled({
                playlistId: sourceId,
                areVoiceTracksEnabled: true,
              }),
            );
            dispatch(
              D.Actions.UseCases.playResumeSource({
                id: sourceId,
                index: trackIndex,
              }),
            );
          },
        };
        yield put(D.Actions.Modal.openModalCoachmarkWithButton(modalConfig));
      }
      isPlayable = canPlayVoiceTrack;
    }
  }

  // Do we need to check if the specified track to play in a sequential playlist is playable?
  if (isPlayable && isInteger(index)) {
    const sourceTrackIds = yield call(getTracksForSource, {
      id,
      type,
      sortOrder,
    });
    const trackId = sourceTrackIds.get(index);
    if (trackId) {
      isPlayable = yield call(checkIsPlayableSourceAndAlertUser, {
        id: trackId,
        sourceId: id,
        alertUserIfUnavailable,
      });
    }
  }
  return isPlayable;
}

export function* checkIsPlayableSource({ data } = {}) {
  const isPlayable = yield call(checkIsPlayableSourceAndAlertUser, data);
  if (!isPlayable) {
    throw new Error('checkIsPlayableSource is false for pandoraId `${pandoraId}`');
  }
}

export function* trackEndError({ payload: { sourceId, token, isRecoverable } }) {
  if (isRecoverable) {
    if (yield select(D.getInDomain('playback.selectors.getCurrentItemPandoraId'))) {
      yield put(
        D.getInDomain('playback.actions.itemEnded')({
          sourceId,
          itemPandoraId: token,
          reason: D.Constants.Playback.APSTrackEndReasons.ERROR,
        }),
      );
    } else {
      yield put(
        D.getInDomain('playback.actions.playTrackFromStation')({
          id: sourceId,
          nextTrackForceNewPlaylist: true,
          forceNext: true,
        }),
      );
    }
  } else {
    // On playback error, we clear any TTM events waiting to be fired to exclude TTM in error cases.
    yield put(D.getInDomain('analytics.actions.timeToMusicClear', noop)());
  }
}
